import React, { useMemo, useState } from 'react';
import { GATE_INFO, X, Z } from './quantum/gates';
import { zeroState, applyGate, applyControlledGate, applySwap, probabilities, measureAll, bitstring, dim } from './quantum/QuantumState';
import QSphere from './QSphere.jsx';

const MAX_QUBITS = 3;
const SINGLE_GATES = ['X', 'Y', 'Z', 'H', 'S', 'T'];
const ROW_HEIGHT = 56;
const SHOT_OPTIONS = [10, 100, 1000, 10000];

const runCircuit = (columns, numQubits) => {
  let state = zeroState(numQubits);
  for (const col of columns) {
    if (col.type === 'single') {
      state = applyGate(state, GATE_INFO[col.gate].matrix, col.qubit, numQubits);
    } else if (col.type === 'cnot') {
      state = applyControlledGate(state, X, col.control, col.target, numQubits);
    } else if (col.type === 'cz') {
      state = applyControlledGate(state, Z, col.control, col.target, numQubits);
    } else if (col.type === 'swap') {
      state = applySwap(state, numQubits, col.qubitA, col.qubitB);
    }
  }
  return state;
};

function CircuitDiagram({ columns, numQubits }) {
  const colWidth = 64;
  return (
    <div className="overflow-x-auto">
      <div className="inline-flex border border-gray-200 rounded-lg bg-gray-50 p-2">
        {/* qubit labels */}
        <div className="flex flex-col justify-around pr-3 border-r border-gray-200 mr-2">
          {Array.from({ length: numQubits }).map((_, q) => (
            <div key={q} style={{ height: ROW_HEIGHT }} className="flex items-center font-mono text-sm font-bold text-gray-700">
              q{q}
            </div>
          ))}
        </div>

        {columns.length === 0 ? (
          <div style={{ height: numQubits * ROW_HEIGHT }} className="flex items-center px-6 text-gray-400 text-sm italic">
            Empty circuit — add a gate below
          </div>
        ) : (
          columns.map((col, ci) => {
            const rows = Array.from({ length: numQubits }).map((_, q) => {
              if (col.type === 'single' && col.qubit === q) return { symbol: GATE_INFO[col.gate].symbol, kind: 'gate', color: '#0078D4' };
              if (col.type === 'cnot' && col.control === q) return { symbol: '●', kind: 'dot', color: '#5C2D91' };
              if (col.type === 'cnot' && col.target === q) return { symbol: '⊕', kind: 'target', color: '#5C2D91' };
              if (col.type === 'cz' && (col.control === q || col.target === q)) return { symbol: '●', kind: 'dot', color: '#db2777' };
              if (col.type === 'swap' && (col.qubitA === q || col.qubitB === q)) return { symbol: '✕', kind: 'swap', color: '#0d9488' };
              return null;
            });
            const linked = [col.control, col.target, col.qubitA, col.qubitB].filter((v) => v !== undefined);
            const connectorTop = linked.length ? Math.min(...linked) * ROW_HEIGHT + ROW_HEIGHT / 2 : 0;
            const connectorHeight = linked.length ? Math.max(...linked) * ROW_HEIGHT + ROW_HEIGHT / 2 - connectorTop : 0;

            return (
              <div key={ci} style={{ width: colWidth, height: numQubits * ROW_HEIGHT }} className="relative flex-shrink-0">
                {linked.length > 0 && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 w-0.5"
                    style={{ top: connectorTop, height: connectorHeight, background: '#9ca3af' }}
                  />
                )}
                {rows.map((cell, q) => (
                  <div key={q} style={{ height: ROW_HEIGHT, top: q * ROW_HEIGHT }} className="absolute inset-x-0 flex items-center justify-center">
                    <div className="absolute inset-x-2 border-t border-gray-300" style={{ top: '50%' }} />
                    {cell && (
                      <div
                        className={`relative z-10 flex items-center justify-center font-bold text-white text-sm ${cell.kind === 'gate' ? 'w-9 h-9 rounded-lg' : 'w-6 h-6 rounded-full'}`}
                        style={{ background: cell.color }}
                      >
                        {cell.symbol}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Histogram({ counts, shots, theoretical, numQubits }) {
  const d = dim(numQubits);
  return (
    <div className="space-y-1.5">
      {Array.from({ length: d }).map((_, i) => {
        const label = bitstring(i, numQubits);
        const measuredPct = shots > 0 ? ((counts[i] || 0) / shots) * 100 : 0;
        const theoreticalPct = theoretical[i] * 100;
        return (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="font-mono text-gray-600 w-14">|{label}⟩</span>
            <div className="flex-1 bg-gray-100 rounded h-5 relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-blue-500" style={{ width: `${measuredPct}%` }} />
              <div className="absolute inset-y-0 border-r-2 border-purple-600" style={{ left: `${theoreticalPct}%` }} title="Theoretical probability" />
            </div>
            <span className="font-mono text-gray-700 w-16 text-right">{measuredPct.toFixed(1)}%</span>
          </div>
        );
      })}
      <div className="flex items-center gap-3 text-[11px] text-gray-500 pt-1">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded-sm inline-block" /> Measured (sampled)</span>
        <span className="flex items-center gap-1"><span className="w-0.5 h-3 bg-purple-600 inline-block" /> Theoretical probability</span>
      </div>
    </div>
  );
}

export default function CircuitBuilder() {
  const [numQubits, setNumQubits] = useState(2);
  const [columns, setColumns] = useState([]);
  const [pendingGate, setPendingGate] = useState('H');
  const [qubitA, setQubitA] = useState(0);
  const [qubitB, setQubitB] = useState(1);
  const [shots, setShots] = useState(1000);
  const [result, setResult] = useState(null); // { counts, shots }

  const isTwoQubitGate = ['CNOT', 'CZ', 'SWAP'].includes(pendingGate);

  const clampQubitSelectors = (n) => {
    setQubitA((a) => Math.min(a, n - 1));
    setQubitB((b) => Math.min(b, n - 1 === 0 ? 0 : n - 1));
  };

  const changeQubitCount = (delta) => {
    const next = Math.max(1, Math.min(MAX_QUBITS, numQubits + delta));
    setNumQubits(next);
    clampQubitSelectors(next);
    setColumns([]);
    setResult(null);
  };

  const addGate = () => {
    setResult(null);
    if (SINGLE_GATES.includes(pendingGate)) {
      setColumns((cols) => [...cols, { type: 'single', gate: pendingGate, qubit: qubitA }]);
      return;
    }
    if (qubitA === qubitB) return; // need two distinct qubits
    if (pendingGate === 'CNOT') setColumns((cols) => [...cols, { type: 'cnot', control: qubitA, target: qubitB }]);
    if (pendingGate === 'CZ') setColumns((cols) => [...cols, { type: 'cz', control: qubitA, target: qubitB }]);
    if (pendingGate === 'SWAP') setColumns((cols) => [...cols, { type: 'swap', qubitA, qubitB }]);
  };

  const removeLast = () => {
    setColumns((cols) => cols.slice(0, -1));
    setResult(null);
  };

  const resetCircuit = () => {
    setColumns([]);
    setResult(null);
  };

  const finalState = useMemo(() => runCircuit(columns, numQubits), [columns, numQubits]);
  const theoreticalProbs = useMemo(() => probabilities(finalState), [finalState]);

  const run = () => {
    const counts = {};
    for (let s = 0; s < shots; s++) {
      const { index } = measureAll(finalState, numQubits);
      counts[index] = (counts[index] || 0) + 1;
    }
    setResult({ counts, shots });
  };

  const qubitOptions = Array.from({ length: numQubits }, (_, q) => q);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-1">Quantum Circuit Builder</h1>
          <p className="text-gray-600">Build a small circuit, run it for N shots, and see the measurement histogram against the theoretical probabilities.</p>
        </div>

        {/* Qubit count */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm mb-4 flex items-center gap-4">
          <span className="text-sm font-bold text-gray-700 uppercase tracking-wide">Qubits</span>
          <div className="flex items-center gap-2">
            <button onClick={() => changeQubitCount(-1)} className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 font-bold text-gray-700">−</button>
            <span className="w-8 text-center font-bold text-gray-900">{numQubits}</span>
            <button onClick={() => changeQubitCount(1)} className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 font-bold text-gray-700">+</button>
          </div>
          <span className="text-xs text-gray-400">(1–{MAX_QUBITS}; changing this resets the circuit)</span>
        </div>

        {/* Circuit diagram */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm mb-4">
          <h2 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">Circuit</h2>
          <CircuitDiagram columns={columns} numQubits={numQubits} />
          <div className="flex gap-2 mt-3">
            <button onClick={removeLast} disabled={columns.length === 0} className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold disabled:opacity-40">
              ↩ Remove last gate
            </button>
            <button onClick={resetCircuit} disabled={columns.length === 0} className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold disabled:opacity-40">
              ↻ Reset circuit
            </button>
          </div>
        </div>

        {/* Add gate */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm mb-4">
          <h2 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">Add a gate</h2>
          <div className="flex flex-wrap gap-2 mb-3">
            {[...SINGLE_GATES, 'CNOT', 'CZ', 'SWAP'].map((g) => (
              <button
                key={g}
                onClick={() => setPendingGate(g)}
                className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${pendingGate === g ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-blue-300'}`}
              >
                {g}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {!isTwoQubitGate ? (
              <label className="flex items-center gap-2 text-sm text-gray-600">
                Apply to
                <select value={qubitA} onChange={(e) => setQubitA(Number(e.target.value))} className="border border-gray-300 rounded-lg px-2 py-1 text-sm">
                  {qubitOptions.map((q) => <option key={q} value={q}>q{q}</option>)}
                </select>
              </label>
            ) : (
              <>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  {pendingGate === 'SWAP' ? 'Qubit A' : 'Control'}
                  <select value={qubitA} onChange={(e) => setQubitA(Number(e.target.value))} className="border border-gray-300 rounded-lg px-2 py-1 text-sm">
                    {qubitOptions.map((q) => <option key={q} value={q}>q{q}</option>)}
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  {pendingGate === 'SWAP' ? 'Qubit B' : 'Target'}
                  <select value={qubitB} onChange={(e) => setQubitB(Number(e.target.value))} className="border border-gray-300 rounded-lg px-2 py-1 text-sm">
                    {qubitOptions.map((q) => <option key={q} value={q}>q{q}</option>)}
                  </select>
                </label>
                {qubitA === qubitB && <span className="text-xs text-red-600">Pick two different qubits</span>}
              </>
            )}
            <button
              onClick={addGate}
              disabled={isTwoQubitGate && qubitA === qubitB}
              className="px-4 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm disabled:opacity-40"
            >
              + Add to circuit
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Run + results */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <h2 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">Run</h2>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-sm text-gray-600">Shots:</span>
              {SHOT_OPTIONS.map((n) => (
                <button
                  key={n}
                  onClick={() => setShots(n)}
                  className={`px-3 py-1 rounded-lg text-sm font-semibold ${shots === n ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}
                >
                  {n.toLocaleString()}
                </button>
              ))}
              <button onClick={run} className="px-5 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm shadow-sm">
                ▶ Run circuit
              </button>
            </div>

            {result && (
              <Histogram counts={result.counts} shots={result.shots} theoretical={theoreticalProbs} numQubits={numQubits} />
            )}
            {!result && (
              <p className="text-gray-400 text-sm italic">Run the circuit to see a measurement histogram here.</p>
            )}
          </div>

          {/* Q-Sphere of the exact same circuit state */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <h2 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide text-center">Q-Sphere (this circuit's state)</h2>
            <QSphere state={finalState} numQubits={numQubits} />
          </div>
        </div>
      </div>
    </div>
  );
}
